import React, { useState, useEffect, useRef } from 'react';
import Typography from '@mui/material/Typography';

import { fonts } from '../gatsby-theme-material-ui-top-layout/theme';
import { PROPY_BLACK } from '../utils';

const TypewriterText = ({ text, speed = 50, ...props }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stop observing once visible
        }
      },
      {
        threshold: 0.1 // Trigger when at least 10% of the component is visible
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return; // Don't start typing until visible

    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, text, speed, isVisible]);

  const sharedStyles = {
    fontFamily: fonts.spaceGrotesk,
    color: PROPY_BLACK,
    letterSpacing: '-0.01em',
    textAlign: props.isConsideredMobile ? 'center' : 'left',
    ...(props.isConsideredMobile && { fontSize: '24px', lineHeight: '24px' }),
  };

  return (
    <div style={{position: 'relative'}} ref={containerRef}>
      {/* Hidden text for layout so that the animated text doesn't cause layout shift */}
      <Typography
        variant="h4"
        sx={{
          ...sharedStyles,
          opacity: 0,
          left: 0,
          right: 0,
          pointerEvents: 'none',
          userSelect: 'none',
          display: 'block',
          whiteSpace: 'pre-wrap',
        }}
        aria-hidden="true"
      >
        {text}
      </Typography>

      {/* Visible animated text */}
      <Typography
        variant="h4"
        sx={{
          ...sharedStyles,
          position: 'absolute',
          top: 0,
          '&::after': {
            content: '"|"',
            opacity: currentIndex < text.length ? 1 : 0,
            animation: 'blink 1s step-end infinite',
          },
          '@keyframes blink': {
            '0%, 100%': {
              opacity: 1,
            },
            '50%': {
              opacity: 0,
            },
          },
        }}
      >
        {displayText}
      </Typography>
    </div>
  );
};

export default TypewriterText;